import {useState, useCallback} from 'react';
import styled from 'styled-components';
import {Button, EMPHASIS} from '@forecast-it/design-system';
import {graphql, useFragment} from 'react-relay';
import type {EditorState} from 'draft-js';
import type {SettingsEmailTab_account$key} from './__generated__/SettingsEmailTab_account.graphql';
import {useSettingsContext} from './SettingsContext';
import {useUpdateAutoReply} from '../hooks/useUpdateAutoReply';
import {useUpdateResolvedAutoReply} from '../hooks/useUpdateResolvedAutoReply';
import {validateAutoReply, autoReplyFieldError, type AutoReplyValidationError} from '../utils/autoReplyValidation';
import {
	validateResolvedAutoReply,
	resolvedAutoReplyFieldError,
	type ResolvedAutoReplyValidationError,
} from '../utils/resolvedAutoReplyValidation';
import {RichTextEditor} from '../../tickets/components/RichTextEditor';
import {MergeTagButton} from '../components/MergeTagButton';
import {insertMergeTag, type MergeTag} from '../utils/insertMergeTag';
import {autoReplyEditorToHtml} from '../utils/autoReplyEditorToHtml';
import {htmlToAutoReplyEditorState} from '../utils/htmlToAutoReplyEditorState';

const Container = styled.div`
	max-width: 28rem;
	padding: 16px;
`;
const Heading = styled.h2`
	font-size: 18px;
	line-height: 28px;
	font-weight: 600;
	color: #0f172a;
	margin: 0;
`;
const Warning = styled.div`
	margin-top: 16px;
	border-radius: 4px;
	border: 1px solid #fcd34d;
	background: #fffbeb;
	padding: 12px 16px;
	font-size: 14px;
	line-height: 20px;
	color: #92400e;
`;
const FieldLabel = styled.label`
	display: block;
	font-size: 12px;
	line-height: 16px;
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	color: #64748b;
`;
const FieldLabelText = styled.span`
	font-size: 14px;
	line-height: 20px;
	font-weight: 500;
	color: #334155;
`;
const ReadOnlyAddress = styled.p`
	margin: 8px 0 0;
	font-family: monospace;
	font-size: 14px;
	line-height: 20px;
	color: #334155;
`;
const HintText = styled.p`
	margin-top: 4px;
	font-size: 12px;
	line-height: 16px;
	color: #64748b;
`;
const Form = styled.form`
	margin-top: 24px;
	> * + * {
		margin-top: 16px;
	}
`;
const SectionFieldset = styled.fieldset`
	border-top: 1px solid #e2e8f0;
	padding-top: 20px;
	& > * + * {
		margin-top: 16px;
	}
`;
const SectionLegend = styled.legend`
	font-size: 14px;
	line-height: 20px;
	font-weight: 600;
	color: #0f172a;
`;
const CheckboxLabel = styled.label`
	display: flex;
	align-items: center;
	gap: 8px;
`;
const Checkbox = styled.input`
	height: 16px;
	width: 16px;
	border-radius: 4px;
	border: 1px solid #cbd5e1;
`;
const CheckboxText = styled.span`
	font-size: 14px;
	line-height: 20px;
	color: #334155;
`;
const BlockLabel = styled.label`
	display: block;
`;
const TextInput = styled.input<{$error?: boolean}>`
	margin-top: 4px;
	width: 100%;
	border-radius: 4px;
	border: 1px solid ${p => (p.$error ? '#f87171' : '#cbd5e1')};
	padding: 8px 12px;
	font-size: 14px;
	line-height: 20px;
	&:disabled {
		background: #f1f5f9;
		color: #94a3b8;
	}
`;
const ReadOnlyEmailInput = styled.input`
	margin-top: 4px;
	width: 100%;
	border-radius: 4px;
	border: 1px solid #cbd5e1;
	background: #f1f5f9;
	padding: 8px 12px;
	font-size: 14px;
	line-height: 20px;
	color: #94a3b8;
`;
const FieldErrorText = styled.p`
	margin-top: 4px;
	font-size: 12px;
	line-height: 16px;
	color: #dc2626;
`;
const BodySection = styled.div<{$disabled?: boolean}>`
	display: block;
	${p => (p.$disabled ? 'pointer-events: none; opacity: 0.5;' : '')}
`;
const BodyHeaderRow = styled.div`
	margin-bottom: 8px;
	display: flex;
	align-items: center;
	justify-content: space-between;
`;
const MutationErrorText = styled.p`
	font-size: 12px;
	line-height: 16px;
	color: #dc2626;
`;
const SavedText = styled.p`
	font-size: 14px;
	line-height: 20px;
	color: #15803d;
	margin: 0;
`;
const FooterRow = styled.div`
	display: flex;
	justify-content: flex-end;
`;

const AccountFragment = graphql`
	fragment SettingsEmailTab_account on TicketingAccount {
		id
		inboundLocalPart
		inboundEmailDomain
		supportEmailDomainConfigured
		autoReply {
			enabled
			fromName
			fromEmail
			bodyHtml
			resolvedEnabled
			resolvedFromName
			resolvedFromEmail
			resolvedBodyHtml
		}
	}
`;

export function SettingsEmailTab() {
	const {account} = useSettingsContext();
	const data = useFragment<SettingsEmailTab_account$key>(AccountFragment, account);
	const {update, loading, error: mutationError, setError} = useUpdateAutoReply();

	const domainConfigured = data.supportEmailDomainConfigured;
	const resolvedInboundEmail =
		data.inboundLocalPart && data.inboundEmailDomain ? `${data.inboundLocalPart}@${data.inboundEmailDomain}` : '';

	const ar = data.autoReply;
	const [enabled, setEnabled] = useState(ar?.enabled ?? false);
	const [fromName, setFromName] = useState(ar?.fromName ?? '');
	const [editorState, setEditorState] = useState<EditorState>(() => htmlToAutoReplyEditorState(ar?.bodyHtml ?? ''));
	const [validationError, setValidationError] = useState<AutoReplyValidationError | null>(null);
	const [saved, setSaved] = useState(false);

	const resolvedFromEmail = resolvedInboundEmail || ar?.fromEmail || '';

	// ACC-551: resolved-trigger auto-reply — independent config saved through its
	// own mutation. The From email is the same server-derived inbound address.
	const {
		update: updateResolved,
		loading: resolvedLoading,
		error: resolvedMutationError,
		setError: setResolvedError,
	} = useUpdateResolvedAutoReply();

	const [resolvedEnabled, setResolvedEnabled] = useState(ar?.resolvedEnabled ?? false);
	const [resolvedFromName, setResolvedFromName] = useState(ar?.resolvedFromName ?? '');
	const [resolvedEditorState, setResolvedEditorState] = useState<EditorState>(() =>
		htmlToAutoReplyEditorState(ar?.resolvedBodyHtml ?? '')
	);
	const [resolvedValidationError, setResolvedValidationError] = useState<ResolvedAutoReplyValidationError | null>(null);
	const [resolvedSaved, setResolvedSaved] = useState(false);

	const handleMergeTagSelect = useCallback((tag: MergeTag) => {
		setEditorState(prev => insertMergeTag(prev, tag));
		setSaved(false);
	}, []);

	function serverFieldMatch(field: string): boolean {
		if (!mutationError) return false;
		return mutationError.toLowerCase().includes(field.toLowerCase());
	}

	async function handleSubmit() {
		setValidationError(null);
		setSaved(false);
		const draft = {
			enabled,
			fromName,
			fromEmail: resolvedFromEmail,
			bodyHtml: autoReplyEditorToHtml(editorState.getCurrentContent()),
		};
		const err = validateAutoReply(draft);
		if (err) {
			setValidationError(err);
			return;
		}
		try {
			await update(draft);
			setSaved(true);
		} catch {
			// error set by hook
		}
	}

	function handleFormSubmit(e: React.FormEvent) {
		e.preventDefault();
		void handleSubmit();
	}

	const handleResolvedMergeTagSelect = useCallback((tag: MergeTag) => {
		setResolvedEditorState(prev => insertMergeTag(prev, tag));
		setResolvedSaved(false);
	}, []);

	function resolvedServerFieldMatch(field: string): boolean {
		if (!resolvedMutationError) return false;
		return resolvedMutationError.toLowerCase().includes(field.toLowerCase());
	}

	async function handleResolvedSubmit() {
		setResolvedValidationError(null);
		setResolvedSaved(false);
		const draft = {
			resolvedEnabled,
			resolvedFromName,
			resolvedFromEmail,
			resolvedBodyHtml: autoReplyEditorToHtml(resolvedEditorState.getCurrentContent()),
		};
		const err = validateResolvedAutoReply(draft);
		if (err) {
			setResolvedValidationError(err);
			return;
		}
		try {
			await updateResolved(draft);
			setResolvedSaved(true);
		} catch {
			// error set by hook
		}
	}

	function handleResolvedFormSubmit(e: React.FormEvent) {
		e.preventDefault();
		void handleResolvedSubmit();
	}

	return (
		<Container>
			<Heading>Email</Heading>

			{!domainConfigured && <Warning>Inbound email domain is not configured. Contact ops.</Warning>}

			<BlockLabel>
				<FieldLabel as="span">Inbound address</FieldLabel>
				{domainConfigured && resolvedInboundEmail ? (
					<>
						<ReadOnlyAddress>{resolvedInboundEmail}</ReadOnlyAddress>
						<HintText>Assigned automatically from your company. Emails sent here become tickets.</HintText>
					</>
				) : (
					<HintText>Not yet assigned.</HintText>
				)}
			</BlockLabel>

			<Form onSubmit={handleFormSubmit} noValidate>
				<SectionFieldset>
					<SectionLegend>Auto-Reply</SectionLegend>

					<CheckboxLabel>
						<Checkbox
							type="checkbox"
							checked={enabled}
							onChange={e => {
								setEnabled(e.target.checked);
								setSaved(false);
								setValidationError(null);
								setError(null);
							}}
						/>
						<CheckboxText>Send auto-reply on new tickets</CheckboxText>
					</CheckboxLabel>

					<BlockLabel>
						<FieldLabelText>From name</FieldLabelText>
						<TextInput
							type="text"
							value={fromName}
							disabled={!enabled}
							onChange={e => {
								setFromName(e.target.value);
								setSaved(false);
								setValidationError(null);
								setError(null);
							}}
							$error={Boolean(
								autoReplyFieldError(validationError, 'fromName') ||
									(mutationError && serverFieldMatch('fromName'))
							)}
						/>
						{autoReplyFieldError(validationError, 'fromName') && (
							<FieldErrorText>{autoReplyFieldError(validationError, 'fromName')}</FieldErrorText>
						)}
					</BlockLabel>

					<BlockLabel>
						<FieldLabelText>From email</FieldLabelText>
						<ReadOnlyEmailInput type="email" value={resolvedFromEmail} disabled readOnly />
						<HintText>Automatically set from your inbound email address.</HintText>
					</BlockLabel>

					<BlockLabel>
						<FieldLabelText>Subject</FieldLabelText>
						<HintText>
							The auto-reply is sent as a reply to the customer&apos;s email (subject &ldquo;Re: &lt;original
							subject&gt;&rdquo;) so it threads into the original conversation.
						</HintText>
					</BlockLabel>

					<BodySection $disabled={!enabled}>
						<BodyHeaderRow>
							<FieldLabelText>Body</FieldLabelText>
							<MergeTagButton onSelect={handleMergeTagSelect} disabled={!enabled} />
						</BodyHeaderRow>
						<RichTextEditor
							editorState={editorState}
							onChange={state => {
								setEditorState(state);
								setSaved(false);
								setError(null);
							}}
							placeholder="Write your auto-reply message..."
						/>
						<HintText>
							Use the {'{ }'} button to insert dynamic fields that resolve when the email is sent.
						</HintText>
					</BodySection>

					{mutationError && <MutationErrorText>{mutationError}</MutationErrorText>}
				</SectionFieldset>

				{saved && <SavedText role="status">Saved</SavedText>}

				<FooterRow>
					<Button emphasis={EMPHASIS.HIGH} onPress={() => void handleSubmit()} disabled={loading}>
						{loading ? 'Saving…' : 'Save'}
					</Button>
				</FooterRow>
			</Form>

			<Form onSubmit={handleResolvedFormSubmit} noValidate>
				<SectionFieldset>
					<SectionLegend>Auto-Reply on Resolved Tickets</SectionLegend>

					<CheckboxLabel>
						<Checkbox
							type="checkbox"
							checked={resolvedEnabled}
							onChange={e => {
								setResolvedEnabled(e.target.checked);
								setResolvedSaved(false);
								setResolvedValidationError(null);
								setResolvedError(null);
							}}
						/>
						<CheckboxText>Send auto-reply on resolved tickets</CheckboxText>
					</CheckboxLabel>

					<BlockLabel>
						<FieldLabelText>From name</FieldLabelText>
						<TextInput
							type="text"
							value={resolvedFromName}
							disabled={!resolvedEnabled}
							onChange={e => {
								setResolvedFromName(e.target.value);
								setResolvedSaved(false);
								setResolvedValidationError(null);
								setResolvedError(null);
							}}
							$error={Boolean(
								resolvedAutoReplyFieldError(resolvedValidationError, 'resolvedFromName') ||
									(resolvedMutationError && resolvedServerFieldMatch('fromName'))
							)}
						/>
						{resolvedAutoReplyFieldError(resolvedValidationError, 'resolvedFromName') && (
							<FieldErrorText>
								{resolvedAutoReplyFieldError(resolvedValidationError, 'resolvedFromName')}
							</FieldErrorText>
						)}
					</BlockLabel>

					<BlockLabel>
						<FieldLabelText>From email</FieldLabelText>
						<ReadOnlyEmailInput type="email" value={resolvedFromEmail} disabled readOnly />
						<HintText>Automatically set from your inbound email address.</HintText>
					</BlockLabel>

					<BlockLabel>
						<FieldLabelText>Subject</FieldLabelText>
						<HintText>
							The auto-reply is sent as a reply to the customer&apos;s email (subject &ldquo;Re: &lt;original
							subject&gt;&rdquo;) so it threads into the original conversation.
						</HintText>
					</BlockLabel>

					<BodySection $disabled={!resolvedEnabled}>
						<BodyHeaderRow>
							<FieldLabelText>Body</FieldLabelText>
							<MergeTagButton onSelect={handleResolvedMergeTagSelect} disabled={!resolvedEnabled} />
						</BodyHeaderRow>
						<RichTextEditor
							editorState={resolvedEditorState}
							onChange={state => {
								setResolvedEditorState(state);
								setResolvedSaved(false);
								setResolvedError(null);
							}}
							placeholder="Write your resolved-ticket auto-reply message..."
						/>
						<HintText>
							Use the {'{ }'} button to insert dynamic fields that resolve when the email is sent.
						</HintText>
					</BodySection>

					{resolvedMutationError && <MutationErrorText>{resolvedMutationError}</MutationErrorText>}
				</SectionFieldset>

				{resolvedSaved && <SavedText role="status">Saved</SavedText>}

				<FooterRow>
					<Button emphasis={EMPHASIS.HIGH} onPress={() => void handleResolvedSubmit()} disabled={resolvedLoading}>
						{resolvedLoading ? 'Saving…' : 'Save'}
					</Button>
				</FooterRow>
			</Form>
		</Container>
	);
}
